import { Role } from "constant/Role"

// Single source of truth for "which procurement menu keys can this role see,
// and which must stay routable but off the sidebar" — replaces the old
// PROCUREMENT_ONLY_KEYS denylist (accessibleRoutes.js), which only ever
// scoped the V2 tree and had no notion of per-role differences beyond
// "ROLE_PROCUREMENT vs everyone else".
//
// `visible` keys render on the sidebar (via getAccessibleRoutes -> Sidenav).
// `hidden` keys are forced to `type: "hidden"` — App.js's getRoutes still
// registers a <Route> for them (it never checks `type`), so detail/form
// screens reached by clicking a table row stay reachable even though their
// parent list item isn't on the menu. A key in neither set is dropped
// entirely — not routable, not visible.

const PM_ORDER_HIDDEN = [
  "pm-order-new",
  "pm-group-order-new",
  "pm-order-detail",
  "pm-group-order-detail",
  "pm-order-edit",
  "pm-order-vendor-proposals",
  "pm-my-order-detail",
  // Landing page both order forms navigate to after a save — without it,
  // creating an order dead-ends.
  "pm-order-requests",
]
// The "create a payment request" flow runs Payment Requests -> "New payment"
// -> To Pay list (/procurement/to-pay) -> pick a PO (/procurement/to-pay/:id)
// -> "Create Payment Request" -> the form. Every step after the list is a
// hidden route, so all of them must stay registered or the flow dead-ends on
// a blank page — including pm-to-pay-detail, which is where the actual
// "Create Payment Request" button lives.
const PM_PAYMENT_HIDDEN = [
  "pm-payment-requests-new",
  "pm-payment-requests-edit",
  "pm-payment-requests-detail",
  "pm-to-pay-detail",
]
const PM_CATALOG_HIDDEN = ["pm-supplier-detail", "pm-items-new", "pm-items-detail", "pm-item-inventory"]

const PROCUREMENT_VISIBLE = [
  "pm-order-group",
  "pm-orders",
  "pm-group-orders",
  // Plain Procurement officers (ROLE_PROCUREMENT) can be named as the approver
  // on an approval-rule step too — not just the Manager role — so they need the
  // Approve Order inbox on their sidebar. The buttons inside are still driven
  // by the order's `allowedActions`, so this only exposes the screen, never
  // widens what any one order lets them do.
  "pm-approvals",
  "pm-purchase-group",
  "pm-purchases",
  "pm-payment-group",
  "pm-payment-requests",
  "pm-suppliers",
  "pm-items",
  "pm-book-inventory",
]
const PROCUREMENT_HIDDEN = [
  ...PM_ORDER_HIDDEN,
  ...PM_PAYMENT_HIDDEN,
  ...PM_CATALOG_HIDDEN,
  "pm-purchases-new",
  "pm-purchases-detail",
  "pm-purchases-edit",
  // Not actually blockable: pm-payment-group's OWN route/component IS
  // ToPayListContainer at this same path (App.js's getRoutes registers a
  // <Route> for a collapse-parent's route unconditionally, independent of
  // which children survive filtering) — so /procurement/to-pay stays
  // reachable by direct URL as long as "pm-payment-group" is visible (it
  // must be, to show Payment Requests). Marking it hidden here is honest
  // bookkeeping, not an actual gate; a hard block would require restructuring
  // that shared group node in routes.js.
  "pm-to-pay",
]

// Staff roles (LXO and every role cloned from the LXO-derived staff menu)
// share one requester-level Procurement surface.
// My Orders only.
// Deliberately NOT visible: procurement-budget (LXO has no budget
// visibility at all), procurement-orders (org-wide), procurement-approvals,
// procurement-group-order-new — LXO can't see other people's orders,
// approve anything, or create a group order.
const STAFF_ACCESS = {
  tree: "v2",
  // Payment is scoped to the LXO's linked centers (`centerScoped` on the v2
  // routes). No To Pay pages — that list is org-wide.
  visible: new Set(["procurement", "procurement-my-orders", "procurement-payment-requests"]),
  hidden: new Set([
    "procurement-payment-requests-new",
    "procurement-payment-requests-edit",
    "procurement-payment-requests-detail",
    "procurement-my-order-detail",
    "procurement-order-new",
    "procurement-order-detail",
    "procurement-order-edit",
    "procurement-order-vendor-proposals",
    // Where OrderRequestFormContainer lands after a successful save. Reads
    // GET /order-requests/my-orders, so it only ever shows the viewer's own
    // orders — safe for LXO. Without it, creating an order dead-ends.
    "procurement-order-requests",
  ]),
}

// Staff surface plus the lists/inboxes the BE scopes to the user's
// departments (GET /order-requests, /purchases, /payment-requests).
export const DEPARTMENT_MANAGER_ACCESS = {
  tree: "v2",
  visible: new Set([
    ...STAFF_ACCESS.visible,
    "procurement-orders",
    "procurement-approvals",
    "procurement-requests",
    "procurement-approve-purchases",
    "procurement-approve-payment-requests",
  ]),
  hidden: new Set([
    ...STAFF_ACCESS.hidden,
    "procurement-approvals-order-detail",
    "procurement-purchases-detail",
  ]),
}

export const PROCUREMENT_MENU_ACCESS = {
  [Role.LXO]: STAFF_ACCESS,
  [Role.LX]: STAFF_ACCESS,
  [Role.SALE]: STAFF_ACCESS,
  [Role.CSO]: STAFF_ACCESS,
  [Role.MKT]: STAFF_ACCESS,
  [Role.CENTER_ADMIN]: STAFF_ACCESS,
  [Role.EC_SM]: STAFF_ACCESS,
  [Role.MANAGEMENT]: STAFF_ACCESS,
  [Role.CALL_CENTRE]: STAFF_ACCESS,
  [Role.CDFM]: STAFF_ACCESS,
  [Role.CENTRE_MANAGER]: STAFF_ACCESS,
  [Role.HSE]: STAFF_ACCESS,
  [Role.IT]: STAFF_ACCESS,
  [Role.LEGAL]: STAFF_ACCESS,
  [Role.TALENT_DEVELOPMENT]: STAFF_ACCESS,
  [Role.TEACHER_TRAINING]: STAFF_ACCESS,
  [Role.AE]: STAFF_ACCESS,
  [Role.CORP]: STAFF_ACCESS,
  [Role.TEACHER]: STAFF_ACCESS,
  [Role.REQUESTER]: STAFF_ACCESS,
  // Order (+ Group Orders), Purchase, Payment Request, Suppliers, Items.
  // No Budget, no My Orders, no Report, no To Pay, no approve-* screens.
  [Role.PROCUREMENT]: {
    tree: "pm-append",
    visible: new Set(PROCUREMENT_VISIBLE),
    hidden: new Set(PROCUREMENT_HIDDEN),
  },
  // Same surface as Procurement, plus the order/purchase approval screens and
  // the full Payment group (To Pay + Approve Payment Request). Approve Payment
  // Request only lists PRs awaiting this user; the buttons stay driven by the
  // approval rule.
  [Role.PROCUREMENT_MANAGER]: {
    tree: "pm-base",
    visible: new Set([
      ...PROCUREMENT_VISIBLE,
      "pm-approvals",
      "pm-approve-purchases",
      "pm-to-pay",
      "pm-approve-payment-requests",
    ]),
    hidden: new Set(PROCUREMENT_HIDDEN.filter((key) => key !== "pm-to-pay")),
  },
  // Budget Control (the oversight/editable view — this key still renders
  // <BudgetOverviewContainer oversight />), Approve Payment Request as the only
  // Payment sub-menu, Suppliers, Items.
  [Role.FINANCE]: {
    tree: "pm-base",
    visible: new Set([
      "pm-budget",
      "pm-budget-report",
      "pm-payment-group",
      "pm-approve-payment-requests",
      "pm-suppliers",
      "pm-items",
      "pm-book-inventory",
    ]),
    // Payment screens link back to their PO/OR — keep those detail pages
    // reachable even though Finance has no PO/Order list of its own. The
    // payment-request list and To Pay stay routable (detail pages navigate
    // back to them) but are off the sidebar.
    hidden: new Set([
      ...PM_PAYMENT_HIDDEN,
      "pm-payment-requests",
      "pm-to-pay",
      ...PM_CATALOG_HIDDEN,
      "pm-purchases-detail",
      "pm-order-detail",
      "pm-item-inventory",
    ]),
  },
}
